import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, Info } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export const ImportModal = ({ open, onOpenChange }) => {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [lastImport, setLastImport] = useState(null);

  useEffect(() => {
    if (open) {
      fetchLastImport();
    }
  }, [open]);

  const fetchLastImport = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/import/metadata`, { headers });
      setLastImport(res.data);
    } catch (error) {
      console.error("Failed to fetch import metadata:", error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      
      const res = await axios.post(`${API}/import?replace_all=${replaceAll}`, formData, { headers });
      setResult(res.data);
      toast.success(`${res.data.added} factures importées avec succès`);
      fetchLastImport();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'import";
      setResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setReplaceAll(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Importer des factures
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Glissez un fichier Excel (.xlsx) ou cliquez pour sélectionner
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`dropzone p-8 rounded-lg text-center cursor-pointer ${isDragActive ? 'active' : ''}`}
            data-testid="dropzone"
          >
            <input {...getInputProps()} />
            <div className="space-y-3">
              {file ? (
                <>
                  <div className="w-14 h-14 mx-auto bg-blue-50 rounded-xl flex items-center justify-center">
                    <FileSpreadsheet className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 mx-auto bg-gray-100 rounded-xl flex items-center justify-center">
                    <Upload className={`w-7 h-7 ${isDragActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="text-foreground">
                      {isDragActive ? 'Déposez le fichier ici' : 'Glissez un fichier ici'}
                    </p>
                    <p className="text-sm text-muted-foreground">ou cliquez pour parcourir</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Replace Option */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <div>
                <Label htmlFor="replace" className="text-sm text-foreground">
                  Remplacer toutes les données
                </Label>
                <p className="text-xs text-muted-foreground">Efface les factures existantes</p>
              </div>
            </div>
            <Switch
              id="replace"
              checked={replaceAll}
              onCheckedChange={setReplaceAll}
              data-testid="replace-switch"
            />
          </div>

          {/* Result */}
          <AnimatePresence mode="wait">
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}
              >
                <div className="flex items-start gap-3">
                  {result.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  )}
                  <div className="flex-1">
                    {result.success ? (
                      <>
                        <p className="font-medium text-foreground">Import réussi</p>
                        <div className="text-sm text-muted-foreground mt-2 space-y-1">
                          <p className="flex items-center gap-2">
                            <span className="text-green-600 font-medium">+{result.added}</span> factures ajoutées
                          </p>
                          <p className="flex items-center gap-2">
                            <span className="text-muted-foreground">{result.skipped}</span> déjà présentes (ignorées)
                          </p>
                          <p className="flex items-center gap-2">
                            <span className="text-blue-600 font-mono font-medium">{formatCurrency(result.total_ht_added)}</span> CA HT ajouté
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-foreground">Erreur d'import</p>
                        <p className="text-sm text-muted-foreground mt-1">{result.error}</p>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Import en cours...</span>
              </div>
              <Progress value={undefined} className="h-1" />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Fermer
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1 btn-primary bg-primary text-primary-foreground"
              data-testid="upload-btn"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></span>
                  Import...
                </span>
              ) : (
                'Importer'
              )}
            </Button>
          </div>

          {/* Last Import Info */}
          {lastImport && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
              <Info className="w-3 h-3" />
              <span>
                Dernier import: {lastImport.filename} le {new Date(lastImport.imported_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportModal;
